import {
  calculateIncomeTax,
  incomeTaxRulesFor,
} from "../income-tax/index.js";
import type { FinancialYear } from "../types.js";
import {
  assertNoFhssHandoff,
  type FhssBenefitInput,
  FhssBenefitInputSchema,
  type FhssBenefitOutput,
  type FhssContributions,
  type FhssEligibilityInput,
  FhssEligibilityInputSchema,
  type FhssEligibilityOutput,
  type FhssGateResult,
  type FhssRuleSet,
} from "./types.js";

// ---------------------------------------------------------------------------
// Pure FHSS calculation surfaces. No I/O, no Date.now(); the assessment FY (and
// so the ruleset) is always a parameter. Every output passes through
// `assertNoFhssHandoff` before it is returned — the C2 AFSL kill-line guard
// (no super-fund / mortgage / credit handoff on a personalised result).
// ---------------------------------------------------------------------------

/** Round to whole dollars. */
function round(n: number): number {
  return Math.round(n);
}

/**
 * Surface 1 — eligibilityReleasable.
 *
 * Evaluates the four one-shot eligibility gates and computes the maximum
 * RELEASABLE contributions, net of:
 *   - the $15,000 per-FY counting cap (voluntary contributions counted in full),
 *   - the $50,000 lifetime counting cap (prior + this year),
 *   - the 85%-concessional / 100%-non-concessional release split.
 *
 * Deemed associated earnings (SIC rate) are NOT computed — they are added on
 * release and surfaced as a fact (`earningsAddedOnRelease`), keeping the rule
 * pure (no per-quarter SIC table in calc).
 */
export function calculateFhssEligibility(
  rawInput: FhssEligibilityInput,
  rules: FhssRuleSet,
): FhssEligibilityOutput {
  const input = FhssEligibilityInputSchema.parse(rawInput);
  const c: FhssContributions = input.contributions;

  // --- Gates (each a question of fact the caller asserts) -------------------
  const neverOwned =
    !input.hasOwnedAuPropertyBefore || input.financialHardshipException;
  const gates: FhssGateResult[] = [
    { gate: "age", pass: input.ageAtDetermination >= rules.minAge },
    { gate: "firstHome", pass: input.firstHome },
    { gate: "neverOwned", pass: neverOwned },
    { gate: "neverReleased", pass: !input.hasPreviouslyReleased },
  ];
  const eligible = gates.every((g) => g.pass);

  // --- Counting test ($15k/yr, $50k lifetime), voluntary in full ------------
  const rawThisYear = c.concessionalThisYear + c.nonConcessionalThisYear;
  // Lifetime headroom under the $50k cap, set by prior years.
  const lifetimeHeadroom = Math.max(
    0,
    rules.lifetimeEligibleCap - c.priorYearsCounted,
  );
  // What can actually COUNT this year is the smaller of the $15k annual cap and
  // the remaining lifetime headroom — a near-cap user can't count $15k if only
  // $2k of lifetime room is left.
  const countingRoom = Math.min(rules.annualEligibleCap, lifetimeHeadroom);
  const countedThisYear = Math.min(rawThisYear, countingRoom);
  // "Over the annual cap" is the slice above the $15k per-FY cap specifically
  // (the headline the UI explains); lifetime exhaustion is surfaced separately
  // via lifetimeHeadroom.
  const overAnnualCap = Math.max(0, rawThisYear - rules.annualEligibleCap);

  // Lifetime: prior counted + this-year counted, clamped at $50k.
  const lifetimeRaw = c.priorYearsCounted + countedThisYear;
  const countedLifetime = Math.min(lifetimeRaw, rules.lifetimeEligibleCap);

  // --- 85%/100% release split -----------------------------------------------
  // The split is applied to the COUNTED contributions, filling the limited
  // counting room (smaller of the annual cap and the lifetime headroom). The
  // lifetime cap MUST clamp the releasable figure — overstating the entitlement
  // for a near-cap user is the ACL misleading-conduct exposure (panel ship-
  // condition #2: show the releasable figure net of the caps).
  //
  // Allocate the counting room NON-CONCESSIONAL first. Non-concessional counts
  // at 100% on release (no 85% haircut), so filling the limited room with it
  // first yields the MAXIMUM honest releasable amount — which is what the ATO's
  // FHSS determination computes (the maximum release amount), not a user-chosen
  // order. Concessional then takes the residual room and wears the 85% haircut.
  const nonConcessionalCounted = Math.min(c.nonConcessionalThisYear, countingRoom);
  const concessionalCounted = Math.min(
    c.concessionalThisYear,
    Math.max(0, countingRoom - nonConcessionalCounted),
  );
  const thisYearReleasable =
    concessionalCounted * rules.concessionalReleaseRate +
    nonConcessionalCounted * rules.nonConcessionalReleaseRate;
  const maxReleasableContributions = round(thisYearReleasable);

  const out: FhssEligibilityOutput = {
    eligible,
    gates,
    countedThisYear,
    overAnnualCap,
    countedLifetime,
    lifetimeHeadroom,
    maxReleasableContributions,
    earningsAddedOnRelease: maxReleasableContributions > 0,
  };
  assertNoFhssHandoff(out);
  return out;
}

/**
 * Effective marginal rate (income-tax bracket rate + Medicare levy rate) for an
 * income, via the shared income-tax engine. This is BOTH the rate the bank
 * account is taxed at and the base of the FHSS release tax (which is Medicare-
 * inclusive per the ATO). We use the engine's `marginalRate` (the bracket rate
 * on the next dollar) and add the FY Medicare levy rate — we never re-derive
 * the bracket schedule here.
 *
 * SIMPLIFICATION (documented, signed off): the Medicare levy is modelled as a
 * flat 2% on the marginal slice for ALL incomes, matching the conventional
 * "marginal rate including Medicare levy" assumption the ATO uses to describe
 * the FHSS release tax. Below the single low-income threshold (~$28k) the real
 * levy is $0 and shades in at 10c/$1 to ~$35k — so for very low earners the 2%
 * here slightly overstates both the bank-account tax and the (already floored-
 * at-0) release tax. That zone is exactly where `lowIncomeErosion` fires and the
 * copy states FHSS gives no benefit, so the simplification never misleads a user
 * TOWARD the scheme (the honest direction). Refining it would need the levy's
 * low-income shade-in here; out of scope for v1.
 */
function effectiveMarginalRateFor(
  taxableIncome: number,
  fy: FinancialYear,
): { marginalRate: number; medicareRate: number } {
  const itRules = incomeTaxRulesFor(fy);
  const out = calculateIncomeTax(
    { taxableIncome, residency: "resident", assessmentYear: fy },
    itRules,
  );
  return { marginalRate: out.marginalRate, medicareRate: itRules.medicareLevy.rate };
}

/**
 * Surface 2 — benefitVsBank.
 *
 * The tax benefit of saving a voluntary CONCESSIONAL contribution via FHSS
 * (15% contributions tax in → marginal-rate − 30% offset out) vs the same gross
 * pre-tax dollars saved in a bank account (taxed at the marginal rate). Returns
 * the contributions-only tax difference as a FACT, plus the deemed-earnings and
 * low-income-erosion honesty caveats. Earnings (SIC) are NOT computed.
 */
export function calculateFhssBenefit(
  rawInput: FhssBenefitInput,
  rules: FhssRuleSet,
): FhssBenefitOutput {
  const input = FhssBenefitInputSchema.parse(rawInput);
  const fy = input.assessmentYear as FinancialYear;

  // FHSS leg is capped at the $15k annual eligible cap.
  const grossContribution = Math.min(input.grossContribution, rules.annualEligibleCap);

  const { marginalRate, medicareRate } = effectiveMarginalRateFor(
    input.taxableIncome,
    fy,
  );
  const effectiveMarginalRate = marginalRate + medicareRate;
  // Release tax = effective marginal rate (Medicare-inclusive) less the 30%
  // offset, floored at 0 (a low earner can't get a negative release tax).
  const releaseTaxRate = Math.max(0, effectiveMarginalRate - rules.releaseTaxOffset);

  // FHSS leg: gross goes in, taxed 15% by the fund; the net slice comes out and
  // is taxed at the release rate.
  const fhssAfterContributionsTax =
    grossContribution * (1 - rules.concessionalContributionsTaxRate);
  const fhssReleaseTax = fhssAfterContributionsTax * releaseTaxRate;
  const fhssNet = fhssAfterContributionsTax - fhssReleaseTax;

  // Bank leg: you save POST-tax dollars — the gross is taxed at the marginal
  // (Medicare-inclusive) rate before it reaches the account.
  const bankNet = grossContribution * (1 - effectiveMarginalRate);

  const contributionsTaxBenefit = fhssNet - bankNet;

  // Low-income erosion: when the effective marginal rate is at/below the 15%
  // contributions-tax rate, the FHSS tax wedge erodes or reverses.
  const lowIncomeErosion =
    effectiveMarginalRate <= rules.concessionalContributionsTaxRate;

  const out: FhssBenefitOutput = {
    grossContribution: round(grossContribution),
    marginalRate,
    medicareRate,
    effectiveMarginalRate,
    releaseTaxRate,
    fhssAfterContributionsTax: round(fhssAfterContributionsTax),
    fhssReleaseTax: round(fhssReleaseTax),
    fhssNet: round(fhssNet),
    bankNet: round(bankNet),
    contributionsTaxBenefit: round(contributionsTaxBenefit),
    earningsCaveat: true,
    lowIncomeErosion,
  };
  assertNoFhssHandoff(out);
  return out;
}
